import { useState, useEffect } from 'react'
import {
  Plus, Pencil, Trash2, X, Users, Eye, EyeOff,
  Copy, Check, RefreshCw, Key, Power, Mail, UserCheck,
} from 'lucide-react'
import type { User, Role } from '../../types/auth.types'
import {
  getAllUsers, updateUser,
  toggleUserActive, generateStrongPassword, logAudit,
} from '../../services/auth.service'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../../context/ToastContext'

const API = 'https://erp.clickbait.services/api'
const ROLES: Role[] = ['founder', 'employee']

function initials(name: string) {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
}

function iStyle(err?: boolean): React.CSSProperties {
  return {
    width: '100%', background: '#1a1a1a',
    border: `1px solid ${err ? 'rgba(204,51,0,0.5)' : 'rgba(255,198,0,0.2)'}`,
    borderRadius: 8, padding: '10px 12px', color: '#fff', fontSize: 13,
    fontFamily: 'DM Sans, sans-serif', outline: 'none',
  }
}

type ModalMode = 'add' | 'edit' | 'delete' | 'credentials' | null

interface FormState {
  name: string; email: string; password: string
  role: Role; reportingTo: string
}

const EMPTY: FormState = { name: '', email: '', password: '', role: 'employee', reportingTo: '' }

interface CredInfo { name: string; email: string; password: string; isReset?: boolean }

function useCopy() {
  const [copied, setCopied] = useState(false)
  const copy = (t: string) => { navigator.clipboard.writeText(t).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000) }) }
  return { copied, copy }
}

// ── Credentials card ──────────────────────────────────────────
function CredModal({ info, onClose }: { info: CredInfo; onClose: () => void }) {
  const { copied: cpPw,  copy: copyPw  } = useCopy()
  const { copied: cpAll, copy: copyAll } = useCopy()
  const [showPw, setShowPw] = useState(true)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-sm rounded-2xl p-6" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.35)' }}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,198,0,0.12)' }}>
              <Key size={15} style={{ color: '#ffc600' }} />
            </div>
            <div>
              <h2 className="text-sm font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
                {info.isReset ? 'Password Reset' : 'Account Created'}
              </h2>
              <p className="text-xs" style={{ color: '#a0a0a0' }}>{info.name}</p>
            </div>
          </div>
          <button onClick={onClose} style={{ color: '#555' }}><X size={18} /></button>
        </div>

        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg mb-4 text-xs"
          style={{ background: 'rgba(255,198,0,0.06)', border: '1px solid rgba(255,198,0,0.2)', color: '#a0a0a0' }}>
          <Mail size={13} className="flex-shrink-0 mt-0.5" style={{ color: '#ffc600' }} />
          <span>Share these credentials securely with <strong style={{ color: '#fff' }}>{info.name}</strong>.</span>
        </div>

        <div className="space-y-3 mb-5">
          <div className="rounded-lg px-3 py-2.5" style={{ background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.12)' }}>
            <p className="text-xs font-semibold mb-0.5" style={{ color: '#555' }}>EMAIL</p>
            <p className="text-sm text-white font-medium">{info.email}</p>
          </div>
          <div className="rounded-lg px-3 py-2.5 flex items-center justify-between gap-2"
            style={{ background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.35)' }}>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold mb-0.5" style={{ color: '#555' }}>PASSWORD</p>
              <p className="text-sm font-mono font-bold" style={{ color: '#ffc600', letterSpacing: showPw ? '0.05em' : '0.2em' }}>
                {showPw ? info.password : '••••••••••••'}
              </p>
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={() => setShowPw(v => !v)} className="p-1.5 rounded" style={{ color: '#555' }}>
                {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
              <button onClick={() => copyPw(info.password)} className="p-1.5 rounded" style={{ color: cpPw ? '#e8a000' : '#ffc600' }}>
                {cpPw ? <Check size={14} /> : <Copy size={14} />}
              </button>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <button onClick={() => copyAll(`Email: ${info.email}\nPassword: ${info.password}`)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-semibold"
            style={{ border: '1px solid rgba(255,198,0,0.25)', color: cpAll ? '#e8a000' : '#ffc600', background: 'transparent' }}>
            {cpAll ? <Check size={14} /> : <Copy size={14} />} {cpAll ? 'Copied!' : 'Copy All'}
          </button>
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg text-sm font-semibold" style={{ background: '#ffc600', color: '#000' }}>
            Done
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main ──────────────────────────────────────────────────────
export default function UserManagement() {
  const { startImpersonation } = useAuth()
  const { toast } = useToast()

  const [users, setUsers]     = useState<User[]>([])
  const [mode, setMode]       = useState<ModalMode>(null)
  const [selected, setSelected] = useState<User | null>(null)
  const [form, setForm]       = useState<FormState>(EMPTY)
  const [err, setErr]         = useState<string | null>(null)
  const [showPw, setShowPw]   = useState(false)
  const [credInfo, setCredInfo] = useState<CredInfo | null>(null)
  const [loading, setLoading] = useState(false)

  const { copied: cpPw, copy: copyPw } = useCopy()

  const founders = users.filter(u => u.role === 'founder')
  const refresh  = () => getAllUsers().then(setUsers)

  useEffect(() => { refresh() }, [])

  useEffect(() => {
    if (mode === 'add' && form.email.includes('@') && !form.password)
      setForm(f => ({ ...f, password: generateStrongPassword() }))
  }, [form.email, mode])

  const openAdd  = () => { setForm(EMPTY); setErr(null); setShowPw(false); setMode('add') }
  const openEdit = (u: User) => {
    setSelected(u)
    setForm({ name: u.name, email: u.email, password: '', role: u.role as Role, reportingTo: u.reportingTo ?? '' })
    setErr(null); setShowPw(false); setMode('edit')
  }
  const openDelete = (u: User) => { setSelected(u); setMode('delete') }
  const closeModal = () => { setMode(null); setSelected(null); setErr(null); setLoading(false) }

  // ── Create user via invite API ────────────────────────────────
  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) { setErr('Name and email are required.'); return }
    if (mode === 'add' && !form.password.trim())  { setErr('Password is required.'); return }
    if (form.role === 'employee' && !form.reportingTo) { setErr('Select a reporting founder.'); return }

    if (mode === 'add') {
      setLoading(true); setErr(null)
      try {
        // 1. Create auth user via backend API
        const res = await fetch(`${API}/invite-user`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email:     form.email.trim().toLowerCase(),
            name:      form.name.trim(),
            role:      form.role,
            reportsTo: form.reportingTo || null,
          }),
        })
        const json = await res.json()
        if (!res.ok) throw new Error(json.error)

        // 2. Set the generated password so employee can log in with email+password
        const uid = json.userId
        if (uid) {
          const r2 = await fetch(`${API}/change-password`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId: uid, newPassword: form.password }),
          })
          const j2 = await r2.json()
          if (!r2.ok) throw new Error(j2.error)
        }

        setCredInfo({ name: form.name.trim(), email: form.email.trim().toLowerCase(), password: form.password })
        setMode('credentials')
        refresh()
        logAudit()
      } catch (e: unknown) {
        setErr(e instanceof Error ? e.message : 'Failed to create user.')
      } finally { setLoading(false) }

    } else if (mode === 'edit' && selected) {
      setLoading(true)
      try {
        await updateUser(selected.id, {
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          reportingTo: form.role === 'employee' ? form.reportingTo : undefined,
        })
        logAudit(); refresh(); closeModal()
        toast(`${form.name} updated`)
      } catch (e: unknown) {
        setErr(e instanceof Error ? e.message : 'Update failed.')
      } finally { setLoading(false) }
    }
  }

  // ── Toggle active ──────────────────────────────────────────
  const handleToggleActive = (u: User) => {
    toggleUserActive(u.id, !u.isActive).then(() => { logAudit(); refresh() })
  }

  // ── Reset password via backend API ────────────────────────
  const handleResetPassword = async (u: User) => {
    const newPwd = generateStrongPassword()
    try {
      const res = await fetch(`${API}/change-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: u.id, newPassword: newPwd }),
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error)
      logAudit()
      setCredInfo({ name: u.name, email: u.email, password: newPwd, isReset: true })
      setMode('credentials')
      toast(`Password reset for ${u.name}`)
    } catch (e: unknown) {
      toast(e instanceof Error ? e.message : 'Password reset failed', 'error')
    }
  }

  // ── Delete user via backend API ───────────────────────────
  const handleDelete = async () => {
    if (!selected) return
    setLoading(true)
    try {
      const res = await fetch(`${API}/delete-user`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: selected.id }),
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error)
      logAudit(); refresh(); closeModal()
      toast(`${selected.name} deleted`, 'info')
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Delete failed.')
    } finally { setLoading(false) }
  }

  const nonAdmin = users.filter(u => u.role !== 'superadmin')
  const roleColor = (r: string) => r === 'founder' ? '#ffc600' : '#a0a0a0'

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Users size={20} style={{ color: '#ffc600' }} />
          <div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>User Management</h1>
            <p className="text-sm" style={{ color: '#a0a0a0' }}>{nonAdmin.length} users · {nonAdmin.filter(u => u.isActive).length} active</p>
          </div>
        </div>
        <button onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
          style={{ background: '#ffc600', color: '#000', boxShadow: '0 0 16px rgba(255,198,0,0.25)' }}>
          <Plus size={15} /> Add User
        </button>
      </div>

      {/* Table */}
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,198,0,0.12)' }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: '#0f0f0f', borderBottom: '1px solid rgba(255,198,0,0.1)' }}>
              {['User', 'Role', 'Reports To', 'Last Login', 'Status', 'Actions'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold" style={{ color: '#a0a0a0' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {nonAdmin.map(u => {
              const manager = founders.find(f => f.id === u.reportingTo)
              return (
                <tr key={u.id} style={{ background: '#0f0f0f', borderBottom: '1px solid rgba(255,198,0,0.06)' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#111')}
                  onMouseLeave={e => (e.currentTarget.style.background = '#0f0f0f')}>

                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                        style={{ background: u.isActive ? roleColor(u.role) : '#333', color: u.isActive ? '#000' : '#555', fontSize: '10px' }}>
                        {initials(u.name)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{u.name}</p>
                        <p className="text-xs" style={{ color: '#555' }}>{u.email}</p>
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 rounded capitalize font-medium"
                      style={{ background: `${roleColor(u.role)}18`, color: roleColor(u.role) }}>
                      {u.role}
                    </span>
                  </td>

                  <td className="px-4 py-3 text-sm" style={{ color: '#a0a0a0' }}>
                    {manager ? manager.name : <span style={{ color: '#333' }}>—</span>}
                  </td>

                  <td className="px-4 py-3 text-xs" style={{ color: '#555' }}>
                    {u.lastLogin
                      ? new Date(u.lastLogin).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
                      : 'Never'}
                  </td>

                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 rounded"
                      style={u.isActive
                        ? { background: 'rgba(232,160,0,0.12)', color: '#e8a000' }
                        : { background: 'rgba(85,85,85,0.2)', color: '#555' }}>
                      {u.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex items-center gap-0.5">
                      {/* Impersonate — log in as */}
                      <button onClick={() => startImpersonation(u)}
                        className="p-1.5 rounded transition-colors" title={`Log in as ${u.name}`}
                        style={{ color: '#4a9eff' }}>
                        <UserCheck size={13} />
                      </button>
                      {/* Edit */}
                      <button onClick={() => openEdit(u)}
                        className="p-1.5 rounded transition-colors" title="Edit user"
                        style={{ color: '#ffc600' }}>
                        <Pencil size={13} />
                      </button>
                      {/* Toggle active */}
                      <button onClick={() => handleToggleActive(u)}
                        className="p-1.5 rounded transition-colors"
                        title={u.isActive ? 'Deactivate' : 'Activate'}
                        style={{ color: u.isActive ? '#e8a000' : '#555' }}>
                        <Power size={13} />
                      </button>
                      {/* Reset password */}
                      <button onClick={() => handleResetPassword(u)}
                        className="p-1.5 rounded transition-colors" title="Reset password"
                        style={{ color: '#a0a0a0' }}>
                        <Key size={13} />
                      </button>
                      {/* Delete */}
                      <button onClick={() => openDelete(u)}
                        className="p-1.5 rounded transition-colors" title="Delete user"
                        style={{ color: '#cc3300' }}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Add / Edit Modal */}
      {(mode === 'add' || mode === 'edit') && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
          <div className="w-full max-w-md rounded-2xl p-6" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.35)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
                {mode === 'add' ? 'Add New User' : `Edit — ${selected?.name}`}
              </h2>
              <button onClick={closeModal} style={{ color: '#555' }}><X size={18} /></button>
            </div>

            {err && (
              <div className="text-xs px-3 py-2 rounded-lg mb-4"
                style={{ background: 'rgba(204,51,0,0.1)', color: '#cc3300', border: '1px solid rgba(204,51,0,0.3)' }}>
                {err}
              </div>
            )}

            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold block mb-1" style={{ color: '#a0a0a0' }}>FULL NAME</label>
                <input style={iStyle()} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Riya Sharma" />
              </div>
              <div>
                <label className="text-xs font-semibold block mb-1" style={{ color: '#a0a0a0' }}>EMAIL</label>
                <input style={iStyle()} value={form.email} type="email"
                  onChange={e => {
                    const v = e.target.value
                    setForm(f => ({ ...f, email: v, password: v.includes('@') && !f.password ? generateStrongPassword() : f.password }))
                  }}
                  placeholder="user@clickbait.in" />
              </div>

              {mode === 'add' && (
                <div>
                  <label className="text-xs font-semibold block mb-1" style={{ color: '#a0a0a0' }}>GENERATED PASSWORD</label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        style={{ ...iStyle(), paddingRight: 64, letterSpacing: showPw ? '0.05em' : '0.12em' }}
                        value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                        type={showPw ? 'text' : 'password'} placeholder="Auto-generated…" />
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                        <button type="button" onClick={() => setShowPw(v => !v)} className="p-1 rounded" style={{ color: '#555' }}>
                          {showPw ? <EyeOff size={13} /> : <Eye size={13} />}
                        </button>
                        <button type="button" onClick={() => copyPw(form.password)} className="p-1 rounded" style={{ color: cpPw ? '#e8a000' : '#ffc600' }}>
                          {cpPw ? <Check size={13} /> : <Copy size={13} />}
                        </button>
                      </div>
                    </div>
                    <button type="button" onClick={() => { setForm(f => ({ ...f, password: generateStrongPassword() })); setShowPw(true) }}
                      className="px-3 py-2 rounded-lg flex items-center gap-1 text-xs font-semibold flex-shrink-0"
                      style={{ background: 'rgba(255,198,0,0.1)', color: '#ffc600', border: '1px solid rgba(255,198,0,0.25)' }}>
                      <RefreshCw size={12} /> New
                    </button>
                  </div>
                </div>
              )}

              {mode === 'add' && (
                <div>
                  <label className="text-xs font-semibold block mb-1" style={{ color: '#a0a0a0' }}>ROLE</label>
                  <select style={iStyle()} value={form.role}
                    onChange={e => setForm(f => ({ ...f, role: e.target.value as Role, reportingTo: '' }))}>
                    {ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                  </select>
                </div>
              )}

              {form.role === 'employee' && (
                <div>
                  <label className="text-xs font-semibold block mb-1" style={{ color: '#a0a0a0' }}>REPORTS TO</label>
                  <select style={iStyle(!form.reportingTo)} value={form.reportingTo}
                    onChange={e => setForm(f => ({ ...f, reportingTo: e.target.value }))}>
                    <option value="">Select founder…</option>
                    {founders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={closeModal} className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                style={{ border: '1px solid rgba(255,198,0,0.25)', color: '#ffc600', background: 'transparent' }}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={loading}
                className="flex-1 py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2"
                style={{ background: loading ? 'rgba(255,198,0,0.5)' : '#ffc600', color: '#000', cursor: loading ? 'not-allowed' : 'pointer' }}>
                {loading && <span className="w-3.5 h-3.5 border-2 border-black/30 border-t-black rounded-full animate-spin inline-block" />}
                {mode === 'add' ? 'Create User' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {mode === 'credentials' && credInfo && (
        <CredModal info={credInfo} onClose={() => { setMode(null); setCredInfo(null) }} />
      )}

      {mode === 'delete' && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
          <div className="w-full max-w-sm rounded-2xl p-6" style={{ background: '#0f0f0f', border: '1px solid rgba(204,51,0,0.35)' }}>
            <Trash2 size={24} className="mb-4" style={{ color: '#cc3300' }} />
            <h2 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>Delete {selected.name}?</h2>
            <p className="text-sm mb-6" style={{ color: '#a0a0a0' }}>
              This permanently removes their account and all sessions. Cannot be undone.
            </p>
            {err && <p className="text-xs mb-4" style={{ color: '#cc3300' }}>{err}</p>}
            <div className="flex gap-3">
              <button onClick={closeModal} className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                style={{ border: '1px solid rgba(255,198,0,0.25)', color: '#ffc600', background: 'transparent' }}>Cancel</button>
              <button onClick={handleDelete} disabled={loading}
                className="flex-1 py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2"
                style={{ background: '#cc3300', color: '#fff', opacity: loading ? 0.6 : 1 }}>
                {loading && <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin inline-block" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
